import { LoginWithCodeResponseDto, QiraUserBE, UpdateUserDto, UserCode } from 'api3';
import { Injectable, inject, signal } from '@angular/core';
import { Api3Service } from '@app/services/api3.service';
import { SchemaValidationService } from '@app/services/schema-validation.service';
import { toError } from '@app/services/error-normalization-utils';
import { environment } from '@env';
import * as Sentry from '@sentry/angular';

/**
 * We store a full AheadUserWithCompany from the server in `currentUser`.
 * Then we add a local "initials" field.
 */
export type QiraUser = QiraUserBE & {
  initials?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isInitializedPromise!: Promise<void>;
  private logoutEventListener?: () => void;

  api3Service = inject(Api3Service);
  schemaValidationService = inject(SchemaValidationService);

  currentUser = signal<QiraUser | undefined>(undefined);
  userIsAdmin = signal(false);
  userIsQiraAdmin = signal(false);

  constructor() {
    this.loadUser();
  }

  handleNotLoggedInError(rawError: unknown) {
    const error = toError(rawError);
    const notLoggedInMessage = 'Invalid or missing JWT';

    if (error.message.includes(notLoggedInMessage)) {
      Sentry.captureMessage('Missing JWT error occurred, redirecting to login page.', {
        tags: {
          location: 'AuthService',
          feature: 'checkForLoggedInError()',
          message: 'Invalid or missing JWT',
        },
      });
      window.location.href = '/login';
    }
  }

  public loadUser(): Promise<void> {
    this.isInitializedPromise = new Promise((resolve) => {
      this._getUserFromServer().finally(() => {
        resolve();
      });
    });
    return this.isInitializedPromise;
  }

  async isLoggedIn() {
    await this.isInitializedPromise;
    return !!this.currentUser();
  }

  login(returnUrl = '/'): void {
    const fullReturnUrl = window.location.origin + returnUrl;
    window.location.href = `${environment.api.url}/auth/ahead/login?returnUrl=${encodeURIComponent(fullReturnUrl)}`;
  }

  logout(returnUrl = '/'): void {
    this.currentUser.set(undefined);
    this.userIsAdmin.set(false);
    this.userIsQiraAdmin.set(false);

    this._clearStoredData();
    this._preventBackNavigationAfterLogout();

    const frontendReturnUrl = window.location.origin + returnUrl;
    window.location.href = `${environment.api.url}/auth/ahead/logout?returnUrl=${encodeURIComponent(frontendReturnUrl)}`;
  }

  public cleanupAfterLogin(): void {
    this._cleanupLogoutListener();
  }

  async loginWithCode(code: string): Promise<LoginWithCodeResponseDto> {
    const { data, error } = await this.api3Service.api.auth.loginWithCode({ code });

    console.log('[AuthService#loginWithCode] loginWithCode data:', data);

    if (error) {
      this.currentUser.set(undefined);
      this.userIsAdmin.set(false);
      console.error('[AuthService#loginWithCode] Error:', error);
      throw error;
    }

    this.cleanupAfterLogin();

    return data;
  }

  async getCodes(): Promise<UserCode[]> {
    const { data, error } = await this.api3Service.api.auth.listCodes();

    if (error) {
      console.error('[AuthService#getCodes] Error:', error);
      throw error;
    }

    const valid = this.schemaValidationService.validate2('auth.listCodes', data);
    if (!valid) {
      console.warn('[AuthService#getCodes] Validation error:', valid);
    }

    return data;
  }

  async deactivateCode(codeId: UserCode['_id']): Promise<boolean> {
    const { error } = await this.api3Service.api.auth.deactivateCode({ id: codeId });

    if (error) {
      console.error('[AuthService#deactivateCode] Error:', error);
      throw error;
    }

    return true;
  }

  async hideCode(codeId: UserCode['_id']): Promise<boolean> {
    const { error } = await this.api3Service.api.auth.hideCode({ id: codeId });

    if (error) {
      console.error('[AuthService#hideCode] Error:', error);
      throw error;
    }

    return true;
  }

  async generateCode(note?: string): Promise<UserCode> {
    const { data, error } = await this.api3Service.api.auth.generateCode({ note });

    if (error) {
      console.error('[AuthService#generateCode] Error:', error);
      throw error;
    }

    return data;
  }

  /**
   * Accept partial updates in the shape of `UpdateUserDto`.
   */
  async updateUser(updateUserDto: Partial<UpdateUserDto>): Promise<void> {
    const { error } = await this.api3Service.api.auth.updateUser(updateUserDto);

    if (error) {
      console.error('[AuthService] Error updating a user profile', updateUserDto, error);
      Sentry.captureException(toError(error), {
        tags: {
          location: 'AuthService',
          feature: 'UserProfile',
          message: 'Failed to update a user profile.',
        },
        extra: updateUserDto,
      });
      throw error;
    }

    await this._getUserFromServer();
  }

  private _clearStoredData(): void {
    try {
      sessionStorage.clear();
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('auth');
    } catch (error) {
      console.warn('Could not clear storage:', error);
    }
  }

  private async _getUserFromServer() {
    const { data: me, error } = await this.api3Service.api.auth.me();

    if (error) {
      this.currentUser.set(undefined);
      this.userIsAdmin.set(false);
      this._clearStoredData();
      console.error('[AuthService#me] Error:', error);
      return;
    }

    if (!me) {
      this.currentUser.set(undefined);
      this.userIsAdmin.set(false);
      return;
    }

    const validationSuccess2 = this.schemaValidationService.validate2('auth.me', me);
    if (!validationSuccess2) {
      console.warn('[AuthService#me] Validation error:', validationSuccess2);
    }

    const user: QiraUser = {
      ...me,
      initials: this._createInitials(me.profile.first_name, me.profile.last_name),
    };

    this.userIsAdmin.set(user.qiraRole === 'ADMIN');
    this.userIsQiraAdmin.set(user.isAdmin === true);

    this._setLocalUser(user);
  }

  private _setLocalUser(user: QiraUser) {
    this.currentUser.set(user);
    this.cleanupAfterLogin();
  }

  private _createInitials(firstName: string, lastName: string): string | undefined {
    if (!firstName && !lastName) return undefined;

    if (firstName && !lastName) {
      return firstName.slice(0, 2).toUpperCase();
    }
    if (lastName && !firstName) {
      return lastName.slice(0, 2).toUpperCase();
    }
    return (firstName?.[0]?.toUpperCase() || '') + (lastName?.[0]?.toUpperCase() || '');
  }

  private _preventBackNavigationAfterLogout(): void {
    if (this.logoutEventListener) return;

    window.history.replaceState(null, '', '/logout');

    this.logoutEventListener = () => {
      window.history.forward();
      window.location.href = '/login';
    };

    window.addEventListener('popstate', this.logoutEventListener);

    setTimeout(() => {
      this._cleanupLogoutListener();
    }, 10000);
  }

  private _cleanupLogoutListener(): void {
    if (this.logoutEventListener) {
      window.removeEventListener('popstate', this.logoutEventListener);
      this.logoutEventListener = undefined;
    }
  }
}
